import './styles.scss'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useErrorBoundary } from 'react-error-boundary'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { Form, Field } from 'react-final-form'
import Loader from 'Components/Loader'
import Validation from 'Utils/Validation'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { getLocalUserDetails, getLocalAuthToken, setLocalWithUserDetails, setLocalWithAuthToken, setLocalWithLoggedInStatus, setLoginOtpFlowVersion } from 'Utils/LocalStorageHandler'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { DialogModal } from 'Components/UIComponents'

const LoginComponent = ({ setLoading, generatedToken, setModalState, resetError, setIsRequestOtpClicked }) => {
  const history = useHistory()
  const { t } = useTranslation()
  const { showBoundary } = useErrorBoundary()
  const mobileNumberInputRef = useRef(null)
  const passwordInputRef = useRef(null)
  const userDetails = getLocalUserDetails()
  const accessToken = getLocalAuthToken()
  const tabId = sessionStorage.tabId ? sessionStorage.tabId : (sessionStorage.tabId = Math.random())

  window.sessionStorage.setItem('tabId', tabId)

  const [isOtpButtonClicked, setIsOtpButtonClicked] = useState(false)
  const [otpExpireTimer, setOtpExpireTimer] = useState(300)
  const [isInvalidEmail, setIsInvalidEmail] = useState(false)
  const [isEmailFormatInvalid, setIsEmailFormatInvalid] = useState(false)
  const [userInputValues, setUserInputValue] = useState({
    email: '',
    otp: ''
  })

  const [validationState, setValidationState] = useState({
    email: false,
    otp: false
  })

  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onDismiss: () => {} })

  const params = new URLSearchParams(history.location.search)
  const authModParam = params.get('authMod')

  const { email, otp } = userInputValues
  const isSubmitButtonDisabled = !email || !otp || otp.length !== 6

  useEffect(() => {
    setValidationState((previousState) => ({ ...previousState, email: false }))
  }, [resetError])

  if (userDetails && accessToken && !authModParam) {
    history.push(ROUTE_STRINGS.coffeequotes)
  } else if (authModParam) {
    localStorage.clear()
  }

  useEffect(() => {
    const handleKeyUp = (e) => {
      if (e.code === 'Enter' && !Validation.isInputFieldEmpty(mobileNumberInputRef.current.value)) {
        passwordInputRef.current.focus()
      }
    }

    const mobileNumberInput = mobileNumberInputRef.current

    mobileNumberInput.addEventListener('keyup', handleKeyUp)

    return () => {
      mobileNumberInput.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  useEffect(() => {
    if (!isOtpButtonClicked) return
    const intervalTimer = setInterval(() => {
      setOtpExpireTimer((pre) => {
        if (pre <= 1) {
          clearInterval(intervalTimer)

          return 0
        }

        return pre - 1
      })
    }, 1000)

    return () => {
      clearInterval(intervalTimer)
    }
  }, [isOtpButtonClicked, otpExpireTimer])

  const authenticateByRequestOtp = async () => {
    setLoading(true)

    try {
      const response = await apiAdapterCoffeeWeb.authenticateByRequestEmailOtp({ email })

      if (!response.data) {
        setIsInvalidEmail(true)
      } else {
        setOtpExpireTimer(300)
        setIsOtpButtonClicked(true)
        setIsRequestOtpClicked(true)
      }
    } catch {
      setModalState({
        showModal: true,
        modalType: 'error',
        message: t('SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN_LATER')
      })
    } finally {
      setLoading(false)
    }
  }

  const authenticateAndProceedToDashboard = async () => {
    setLoading(true)
    const { email, otp } = userInputValues
    const postData = {
      username: email,
      includeMenu: true,
      otp: otp
    }

    try {
      const {
        data,
        data: { isError, userType }
      } = await apiAdapterCoffeeWeb.authenticateAdminUser(postData)

      if (!isError) {
        if (userType === 0) {
          setLoginOtpFlowVersion('otp-v1')
          setLocalWithUserDetails(data)
          setLocalWithAuthToken(generatedToken)
          setLocalWithLoggedInStatus(true)
          history.push(ROUTE_STRINGS.coffeequotes)
          setLoading(true)
        }
      } else {
        setLoading(false)
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('OTP_MISS_MATCH')
        })
      }
    } catch (error) {
      setModalState({
        showModal: true,
        modalType: 'error',
        message: t('SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN_LATER')
      })
      showBoundary(error)
    } finally {
      setLoading(false)
    }
  }

  const handleLoginButton = () => {
    const { email, otp } = userInputValues

    if (email && otp) {
      authenticateAndProceedToDashboard()
    }
  }

  const handleChange = ({ target: { name, value } }) => {
    setIsInvalidEmail(false)

    setValidationState((prev) => ({
      ...prev,
      [name]: false
    }))

    let sanitizedValue = value

    if (name === 'otp') {
      sanitizedValue = value.replace(/\D/g, '')
    } else if (name === 'email') {
      sanitizedValue = value.toLowerCase()
    }

    setUserInputValue((prev) => ({
      ...prev,
      [name]: sanitizedValue
    }))

    if (name === 'email') {
      setIsEmailFormatInvalid(!Validation.emailValidation(sanitizedValue))
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target

    if ((name === 'email' || name === 'otp') && (validationState[name] === '' || value === '')) {
      setValidationState((prevValidationState) => ({ ...prevValidationState, [name]: true }))
    }
  }

  const handleDismiss = () => {
    setShowDialog({
      ...showDialog,
      showModal: false
    })
    setUserInputValue({ ...userInputValues, otp: '' })
  }

  const onSubmit = (data) => {
    setUserInputValue(data)
  }

  const handleBackButton = () => {
    setIsRequestOtpClicked(false)
    setIsOtpButtonClicked(false)
    setUserInputValue({ ...userInputValues, otp: '' })
    setValidationState({ ...validationState, otp: false })
  }

  const otpExpireMinutes = String(Math.floor(otpExpireTimer / 60)).padStart(2, '0')
  const otpExpireSeconds = String(otpExpireTimer % 60).padStart(2, '0')

  return (
    <>
      <div className="login-form" data-testid="login-form">
        <Form
          onSubmit={onSubmit}
          initialValues={userInputValues}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} className="p-fluid">
              <div className="email-container">
                <Field
                  name="email"
                  render={({ input, meta }) => (
                    <div className="field">
                      <div className="p-inputgroup flex-1">
                        <span className="p-inputgroup-addon">
                          <i className="pi pi-user" />
                        </span>

                        <span className="p-float-label input-with-icon">
                          <InputText placeholder={t('EMAIL')} {...input} id="email" onBlur={handleBlur} value={userInputValues.email} onChange={handleChange} ref={mobileNumberInputRef} className={validationState.email ? 'p-invalid' : ''} disabled={isOtpButtonClicked} />
                          <label htmlFor="email" className={validationState.email ? 'p-error label-text' : 'label-text'}>
                            {t('EMAIL')} <span className="required-star"> *</span>
                          </label>

                          {isOtpButtonClicked && (
                            <span className="edit-icon" onClick={handleBackButton}>
                              <i className="pi pi-pencil"></i>
                            </span>
                          )}
                        </span>
                      </div>

                      {validationState?.email && <small className="p-error">{t('EMAIL_IS_REQUIRED')}</small>}
                      {isEmailFormatInvalid && email && !validationState?.email && <small className="p-error">{t('INVALID_EMAIL_FORMAT')}</small>}
                      {isInvalidEmail && <small className="p-error">{t('EMAIL_DOES_NOT_EXIST')}</small>}
                    </div>
                  )}
                />
              </div>

              {isOtpButtonClicked && !isInvalidEmail && (
                <>
                  <Field
                    name="otp"
                    render={({ input, meta }) => (
                      <div className="field">
                        <div className="p-inputgroup otp-field">
                          <span className="p-inputgroup-addon">
                            <i className="pi pi-lock-open" />
                          </span>
                          <span className="p-input-icon-right">
                            <span className="p-float-label">
                              <InputText name="otp" {...input} onBlur={handleBlur} maxLength={6} value={userInputValues.otp} onChange={handleChange} className={validationState.otp ? 'p-invalid' : ''} />
                              <label htmlFor="otp" className={validationState.otp ? 'p-error label-text otp-label-text' : 'label-text otp-label-text'}>
                                {t('OTP')} <span className="required-star"> *</span>
                              </label>
                            </span>
                          </span>
                        </div>
                        {validationState.otp && (
                          <div>
                            <small className="p-error">{t('OTP_IS_REQUIRED')}</small>{' '}
                          </div>
                        )}
                        <div className="otp-bottom-info">
                          <button
                            className="resend-text"
                            style={{
                              cursor: otpExpireTimer > 0 ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => {
                              authenticateByRequestOtp()
                            }}
                            disabled={otpExpireTimer > 0}
                          >
                            {t('RESEND_OTP')}
                          </button>
                          {otpExpireTimer > 0 && <span className="timer-text">{`${t('OTP_WILL_EXPIRE_IN')} ${otpExpireMinutes}:${otpExpireSeconds}`}</span>}
                        </div>
                      </div>
                    )}
                  />
                </>
              )}
            </form>
          )}
        />
        {!isOtpButtonClicked ? (
          <Button label={t('OTP_REQUEST')} onClick={authenticateByRequestOtp} className="login-button" disabled={!email || isEmailFormatInvalid || isInvalidEmail} />
        ) : (
          <>
            <Button label={t('SUBMIT')} onClick={handleLoginButton} className="login-button" disabled={isSubmitButtonDisabled} />
          </>
        )}
      </div>
      <DialogModal {...showDialog} onDismiss={handleDismiss} onHide={handleDismiss} />
    </>
  )
}

export default Loader(LoginComponent)
